type Delimiter = '.';
const delimiter: Delimiter = '.';
type Feature = string;
type Experiment = `${string}${Delimiter}${string}`;

/** A sorted array of features and experiments expressed as strings */
export type OptimizelyState = (Feature | Experiment)[];
export type OptimizelyStateMap = {
    experiments: Record<string, string | null>;
    features: Record<string, boolean>;
};

export const asMap = (state: OptimizelyState): OptimizelyStateMap => {
    const map: OptimizelyStateMap = { experiments: {}, features: {} };
    for (const kv of state) {
        const [k, v] = kv.split(delimiter);
        if (v) {
            map.experiments[k] = v;
        } else {
            map.features[k] = true;
        }
    }
    return map;
};

export const flattenMap = (
    stateMap: OptimizelyStateMap,
    filter: (key: string) => boolean = () => true
): OptimizelyState => {
    const state = [];
    for (const [k, v] of Object.entries(stateMap.experiments)) {
        if (v !== null && filter(k)) {
            state.push([k, v].join(delimiter));
        }
    }
    for (const [k, v] of Object.entries(stateMap.features)) {
        if (v === true && filter(k)) {
            state.push(k);
        }
    }
    return state.sort();
};

export const combineMaps = (
    ...stateMaps: (OptimizelyStateMap | undefined)[]
): OptimizelyStateMap => {
    const hashMap: {
        experiments: Record<string, string | null>;
        features: Record<string, boolean>;
    } = { experiments: {}, features: {} };
    const stateMap: OptimizelyStateMap = { experiments: {}, features: {} };
    for (let i = stateMaps.length - 1; i >= 0; i--) {
        if (undefined !== stateMaps[i]) {
            for (const [key, val] of Object.entries(
                (stateMaps[i] as OptimizelyStateMap)?.features
            )) {
                if (key in hashMap.features) continue;
                hashMap.features[key] = val;
                if (val === false) continue;
                stateMap.features[key] = val;
            }
            for (const [key, val] of Object.entries(
                (stateMaps[i] as OptimizelyStateMap)?.experiments
            )) {
                if (key in hashMap.experiments) continue;
                hashMap.experiments[key] = val;
                if (val === null) continue;
                stateMap.experiments[key] = val;
            }
        }
    }
    return stateMap;
};

export const combine = (
    ...states: (OptimizelyState | undefined)[]
): OptimizelyState => {
    const hashMap: Record<string, unknown> = {};
    const state: string[] = [];
    for (let i = states.length - 1; i >= 0; i--) {
        if (undefined !== states[i]) {
            for (const kv of states[i] as OptimizelyState) {
                if (kv === '') continue; /* ignore empty states */
                const [key, val] = kv.split(delimiter);
                if (key in hashMap) continue;
                hashMap[key] = val ?? '';
                state.push(kv);
            }
        }
    }
    return state.sort();
};
